import { CoinflipChoice, MiniGameStatus } from '@/__generated__/graphql'
import { Box, Icon } from '@chakra-ui/react'
import { useRef, useCallback, useEffect, memo, forwardRef } from 'react'
import CoinHeadIcon from '@/public/assets/images/coinflip/coin-head.svg'
import CoinTailIcon from '@/public/assets/images/coinflip/coin-tail.svg'
import { AnimationState } from './types'
import { useCoinflipStore } from './CoinflipProvider'
import { SOUNDS, useAudio } from '@/context/AudioProvider'

const enteringKeyframes: Record<CoinflipChoice, Array<{ transform: string; easing?: string }>> = {
  [CoinflipChoice.Heads]: [
    {
      transform: 'rotateY(0deg) scale(1)',
    },
    {
      transform: 'rotateY(0deg) scale(0.75)',
    },
    {
      transform: 'rotateY(360deg) scale(1.25)',
    },
  ],
  [CoinflipChoice.Tails]: [
    {
      transform: 'rotateY(180deg) scale(1)',
    },
    {
      transform: 'rotateY(180deg) scale(0.75)',
    },
    {
      transform: 'rotateY(540deg) scale(1.25)',
    },
  ],
}

const ongoingKeyframes: Record<CoinflipChoice, Array<{ transform: string }>> = {
  [CoinflipChoice.Heads]: [{ transform: 'rotateY(360deg) scale(1.25)' }, { transform: 'rotateY(720deg) scale(1.25)' }],
  [CoinflipChoice.Tails]: [{ transform: 'rotateY(540deg) scale(1.25)' }, { transform: 'rotateY(900deg) scale(1.25)' }],
}

const finishingKeyframes: Record<CoinflipChoice, Record<CoinflipChoice, Array<{ transform: string }>>> = {
  [CoinflipChoice.Heads]: {
    [CoinflipChoice.Heads]: [{ transform: 'rotateY(720deg) scale(1.25)' }, { transform: 'rotateY(1440deg) scale(1)' }],
    [CoinflipChoice.Tails]: [{ transform: 'rotateY(720deg) scale(1.25)' }, { transform: 'rotateY(1620deg) scale(1)' }],
  },
  [CoinflipChoice.Tails]: {
    [CoinflipChoice.Heads]: [{ transform: 'rotateY(900deg) scale(1.25)' }, { transform: 'rotateY(1440deg) scale(1)' }],
    [CoinflipChoice.Tails]: [{ transform: 'rotateY(900deg) scale(1.25)' }, { transform: 'rotateY(1620deg) scale(1)' }],
  },
}

const resultKeyframes = {
  [MiniGameStatus.Win]: [
    {
      background: '#68F276',
      opacity: 1,
      transformOrigin: '50% 50%',
      transform: 'scale(1)',
      filter: 'blur(0)',
    },
    {
      background: '#68F276',
      opacity: 0,
      transformOrigin: '50% 50%',
      transform: 'scale(1.2)',
      filter: 'blur(20px)',
    },
  ],
  [MiniGameStatus.Lose]: [
    {
      background: '#FF7D7D',
      opacity: 1,
      transformOrigin: '50% 50%',
      transform: 'scale(1)',
      filter: 'blur(0)',
    },
    {
      background: '#FF7D7D',
      opacity: 0,
      transformOrigin: '50% 50%',
      transform: 'scale(1.2)',
      filter: 'blur(20px)',
    },
  ],
  [MiniGameStatus.Pending]: [],
}

const errorKeyframes: Record<CoinflipChoice, Array<{ transform: string; opacity?: number }>> = {
  [CoinflipChoice.Heads]: [
    { transform: 'rotateY(720deg) scale(1.25)' },
    { transform: 'rotateY(1080deg) scale(1) translateY(-50px)' },
    { transform: 'rotateY(1080deg) scale(1) translateY(50px)' },
    { transform: 'rotateY(1080deg) scale(1) translateY(0px)' },
  ],
  [CoinflipChoice.Tails]: [
    { transform: 'rotateY(900deg) scale(1.25)' },
    { transform: 'rotateY(1260deg) scale(1) translateY(-50px)' },
    { transform: 'rotateY(1260deg) scale(1) translateY(50px)' },
    { transform: 'rotateY(1260deg) scale(1) translateY(0px)' },
  ],
}

const errorWrapperKeyframes: Record<CoinflipChoice, Array<{ opacity?: number }>> = {
  [CoinflipChoice.Heads]: [{ opacity: 1 }, { opacity: 0 }, { opacity: 0 }, { opacity: 1 }],
  [CoinflipChoice.Tails]: [{ opacity: 1 }, { opacity: 0 }, { opacity: 0 }, { opacity: 1 }],
}

const startAnimation = async (el: HTMLDivElement, keyframes: any, options: any) => {
  return await new Promise((resolve) => {
    const animation = el.animate(keyframes, options)

    animation.onfinish = () => {
      resolve(true)
    }
  })
}

export const Coin = memo(() => {
  const ref = useRef<HTMLDivElement>(null)
  const wrapper = useRef<HTMLDivElement>(null)
  const sideRef = useRef<{ prev: CoinflipChoice; current: CoinflipChoice } | null>(null)
  const stateRef = useRef<AnimationState | null>(null)
  const { animationSpeed, animationSide, animationState, stopAnimation, dispatch, getHistory } = useCoinflipStore()

  const { play } = useAudio()

  const coinRenderCallback = useCallback(() => {
    ref.current!.style.transform = animationSide.current === CoinflipChoice.Heads ? 'rotateY(0deg)' : 'rotateY(180deg)'
  }, [])

  useEffect(() => {
    sideRef.current = animationSide

    if (stateRef.current === AnimationState.DRAWING) {
      stateRef.current = AnimationState.FINISHING
    }
  }, [animationSide])

  useEffect(() => {
    if (animationState === AnimationState.STARTED) {
      handleFlip()
    }

    if (animationState === AnimationState.ERROR) {
      stateRef.current = AnimationState.ERROR
    }
  }, [animationState])

  const handleFlip = useCallback(async () => {
    stateRef.current = AnimationState.DRAWING
    play(SOUNDS.COINFLIP_START)
    await startAnimation(ref.current!, enteringKeyframes[sideRef.current!.current], {
      duration: 1_000 * animationSpeed,
      iterations: 1,
      easing: 'ease-in',
      fill: 'forwards',
    })

    while (stateRef.current == AnimationState.DRAWING) {
      await startAnimation(ref.current!, ongoingKeyframes[sideRef.current!.current], {
        duration: 500 * animationSpeed,
        iterations: 1,
        easing: 'linear',
        fill: 'forwards',
      })
    }

    if (stateRef.current == AnimationState.FINISHING) {
      setTimeout(() => {
        startAnimation(ref.current?.querySelector('.coinbox')!, resultKeyframes[getHistory().at(0)!.result], {
          duration: 200 * animationSpeed,
          iterations: 1,
        })
      }, 900)

      await startAnimation(ref.current!, finishingKeyframes[sideRef.current!.prev][sideRef.current!.current], {
        duration: 1000 * animationSpeed,
        iterations: 1,
        easing: 'ease-out',
        fill: 'forwards',
      })
    } else if (stateRef.current == AnimationState.ERROR) {
      startAnimation(ref.current!, errorKeyframes[sideRef.current!.current], {
        duration: 1_000 * animationSpeed,
        iterations: 1,
        easing: 'ease-out',
        fill: 'forwards',
      })

      startAnimation(wrapper.current!, errorWrapperKeyframes[sideRef.current!.current], {
        duration: 1_000 * animationSpeed,
        iterations: 1,
        easing: 'ease-in',
        fill: 'forwards',
      })

      play(SOUNDS.PLAYER_BET_ERROR)
    }

    if (useCoinflipStore.getState().lastRoundResult === MiniGameStatus.Win) {
      play(SOUNDS.COINFLIP_WIN)
    } else {
      play(SOUNDS.COINFLIP_LOSE)
    }

    stopAnimation()
    dispatch()
  }, [animationSpeed])

  return (
    <Box ref={wrapper}>
      <AnimatedCoin ref={ref} renderCallback={coinRenderCallback} />
    </Box>
  )
})

const AnimatedCoin = memo(
  forwardRef<any, { renderCallback: () => void }>(({ renderCallback }, ref) => {
    useEffect(() => {
      renderCallback()
    }, [])

    return (
      <Box
        ref={ref}
        pos={'relative'}
        h={{ base: 28, md: 40 }}
        w={{ base: 28, md: 40 }}
        zIndex={100000}
        css={{
          transformStyle: 'preserve-3d',
          perspective: '1000px',
          transformOrigin: 'center',
        }}
      >
        <Box className="coinbox" opacity={0} pos={'absolute'} rounded={'full'} inset={1} />
        <Icon
          width={{ base: 28, md: 40 }}
          height={{ base: 28, md: 40 }}
          css={{ backfaceVisibility: 'hidden' }}
          pos={'absolute'}
          transform={'rotateY(180deg)'}
          as={CoinTailIcon}
          alt=""
        />
        <Icon
          width={{ base: 28, md: 40 }}
          height={{ base: 28, md: 40 }}
          css={{ backfaceVisibility: 'hidden' }}
          pos={'absolute'}
          transform={'rotateY(0deg)'}
          as={CoinHeadIcon}
          alt=""
        />
      </Box>
    )
  })
)
